import { Navigate, useRouter } from '@tanstack/react-router';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { BreadcrumbItem } from '@/services/breadcrumbs';
import type { Delivery } from '@/services/db/delivery/entity';
import { getDeliveryState } from '@/services/db/delivery/utils';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';
import Journeys from '@/pages/journeys';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import { DeliveryDetailPage } from './delivery-detail';
import OutboxPage from './index.page';
import type { OutboxTab } from './types';

/** The Drafts tab's URL segment — unchanged across both segments. */
const DRAFTS_TAB = 'drafts';

/** The Outbox landing segment — Ember redirects it (and the index) to the default (Sent) tab. */
const WELCOME_SEGMENT = 'welcome';

/**
 * The Outbox's URL segments, each with its "Sent"-tab name and its splat route. The
 * `OutboxSegment` type derives from this catalog, so the segment set is defined in one place.
 */
const OUTBOX_SEGMENTS = {
  outbox: { sentTab: 'deliveries', splatRoute: '/workspaces/$workspaceId/journeys/outbox/$' },
  message_activity: { sentTab: 'sent', splatRoute: '/workspaces/$workspaceId/journeys/message_activity/$' },
} as const satisfies Record<string, { sentTab: string; splatRoute: string }>;

export type OutboxSegment = keyof typeof OUTBOX_SEGMENTS;

/** The URL segment for a logical tab under a given segment. */
function tabSegmentFor(segment: OutboxSegment, tab: OutboxTab): string {
  return tab === 'drafts' ? DRAFTS_TAB : OUTBOX_SEGMENTS[segment].sentTab;
}

/** Every Sent-tab spelling (`deliveries`/`sent`), so a legacy URL is recognized. */
const SENT_TAB_NAMES: ReadonlySet<string> = new Set(Object.values(OUTBOX_SEGMENTS).map((s) => s.sentTab));

/** Collapse leading/trailing/duplicate slashes so a splat compares equal to its canonical form. */
function normalizeSplat(splat: string): string {
  return splat
    .split('/')
    .filter((part) => part.length > 0)
    .join('/');
}

/** What React renders for a splat path: the tab list, a single detail, or an Ember hand-off. */
type OutboxView = { kind: 'ember' } | { kind: 'list'; tab: OutboxTab } | { kind: 'detail'; tab: OutboxTab; id: string };

/** The logical tab a first URL segment names (either Sent-tab spelling → deliveries), or null if none. */
function tabForSegment(first: string): OutboxTab | null {
  if (SENT_TAB_NAMES.has(first)) {
    return 'deliveries';
  }
  return first === DRAFTS_TAB ? 'drafts' : null;
}

/**
 * Classify the splat path. React owns the Deliveries/Sent + Drafts tab lists, the Deliveries/Sent
 * `:id` detail, and the landing paths — the bare index and `/welcome` both resolve to the default
 * (Sent) tab, matching Ember's redirects. Either Sent-tab spelling (`deliveries`/`sent`) is
 * accepted so a cross-rename URL normalizes. The draft detail/edit and any deeper path hand off to
 * the embedded Ember host. The caller redirects any non-canonical result to its canonical URL.
 */
function classifyOutboxPath(splat: string): OutboxView {
  const pathSegments = splat.split('/').filter((part) => part.length > 0);
  const first = pathSegments[0];
  const rest = pathSegments.slice(1);
  // Index and terminal `/welcome` land on the default Sent tab (Ember: index → deliveries,
  // welcome → index). `welcome` is terminal in the ownership regex, so a deeper `welcome/...`
  // path is `embedded` there — fall through to the Ember hand-off below to keep them in agreement.
  if (first === undefined || (first === WELCOME_SEGMENT && rest.length === 0)) {
    return { kind: 'list', tab: 'deliveries' };
  }
  const tab = tabForSegment(first);
  if (tab == null) {
    return { kind: 'ember' };
  }
  if (rest.length === 0) {
    return { kind: 'list', tab };
  }
  if (tab === 'deliveries' && rest.length === 1) {
    return { kind: 'detail', tab, id: rest[0] };
  }
  return { kind: 'ember' };
}

/** The resolved routing decision for an Outbox splat — the React-free core, so it unit-tests directly. */
type OutboxResolution =
  | { kind: 'ember' }
  | { kind: 'redirect'; splat: string }
  | { kind: 'list'; tab: OutboxTab }
  | { kind: 'detail'; tab: OutboxTab; id: string };

/**
 * Resolve an incoming (`segment`, `splat`) against the canonical segment: hand off to Ember,
 * redirect to the canonical URL, or render an already-canonical tab list / detail. The `redirect`
 * splat covers the segment swap, a legacy tab name (`outbox/sent` → `outbox/deliveries`), and the
 * index / `/welcome` landing paths (→ the default Sent tab).
 */
export function resolveOutboxRoute(
  segment: OutboxSegment,
  splat: string,
  canonicalSegment: OutboxSegment,
): OutboxResolution {
  const view = classifyOutboxPath(splat);
  if (view.kind === 'ember') {
    return { kind: 'ember' };
  }
  const canonicalTab = tabSegmentFor(canonicalSegment, view.tab);
  const canonicalSplat = view.kind === 'detail' ? `${canonicalTab}/${view.id}` : canonicalTab;
  if (segment !== canonicalSegment || normalizeSplat(splat) !== canonicalSplat) {
    return { kind: 'redirect', splat: canonicalSplat };
  }
  return view;
}

type OutboxRouteViewProps = {
  segment: OutboxSegment;
  workspaceId: WorkspaceIdRouteParamInput;
  /** The path after `/journeys/<segment>/` (e.g. `deliveries`, `drafts`, `deliveries/<id>`). */
  splat: string;
};

/**
 * Gate + render for the Outbox subtree, across both segments (`outbox` legacy,
 * `message_activity` canonical). Migrated to React: the Deliveries/Sent tab (list + a single
 * `:id` detail) and the Drafts tab (list only), plus the index + `/welcome` landing paths, which
 * redirect to the default tab. The draft detail/edit and any deeper path fall through to the
 * embedded Ember host.
 */
export function OutboxRouteView({ segment, workspaceId: workspaceIdParam, splat }: OutboxRouteViewProps) {
  const router = useRouter();
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  const canonicalSegment: OutboxSegment = 'message_activity';

  // The migrated pages read the workspace via `requireWorkspaceId()` (a URL parse) that can't
  // resolve the `/workspaces/last` sentinel, so resolve it to the real id before they render.
  // A no-op for numeric workspace URLs.
  if (workspaceId === 'last') {
    return currentWorkspace == null ? null : (
      <Navigate
        to={OUTBOX_SEGMENTS[segment].splatRoute}
        params={{ workspaceId: String(currentWorkspace.id), _splat: splat }}
        search
        hash
        replace
      />
    );
  }

  const resolution = resolveOutboxRoute(segment, splat, canonicalSegment);
  if (resolution.kind === 'ember') {
    return <Journeys />;
  }

  // Redirect to the canonical URL (segment swap / legacy tab name / index / welcome),
  // preserving `search` + `hash` across the bounce.
  if (resolution.kind === 'redirect') {
    return (
      <Navigate
        to={OUTBOX_SEGMENTS[canonicalSegment].splatRoute}
        params={{ workspaceId, _splat: resolution.splat }}
        search
        hash
        replace
      />
    );
  }

  const splatHref = (targetSplat: string) =>
    router.buildLocation({
      to: OUTBOX_SEGMENTS[canonicalSegment].splatRoute,
      params: { workspaceId, _splat: targetSplat },
    }).href;

  if (resolution.kind === 'detail') {
    // Each related row links to its own delivery: a drafted sibling routes to the
    // Ember-owned draft path, everything else to the React detail (Ember picked
    // `draft.index` vs `outbox.deliveries.delivery` the same way).
    const relatedDeliveryHref = (related: Delivery) => {
      const tab = getDeliveryState(related) === 'drafted' ? DRAFTS_TAB : OUTBOX_SEGMENTS[canonicalSegment].sentTab;
      return splatHref(`${tab}/${related.id}`);
    };
    // Ember's `outbox` + `outbox.deliveries` route crumbs, both pointing at the Sent-tab list
    // (the delivery-subject crumb is appended by the detail page once it loads).
    const listHref = splatHref(tabSegmentFor(canonicalSegment, 'deliveries'));
    const breadcrumbs: BreadcrumbItem[] = [
      { id: 'outbox', text: 'Message activity', href: listHref },
      { id: 'outbox-deliveries', text: 'Sent', href: listHref },
    ];
    return (
      <AppErrorBoundary resetKeys={[resolution.id]}>
        <RumPageRendered>
          <DeliveryDetailPage
            deliveryId={resolution.id}
            getDeliveryHref={relatedDeliveryHref}
            breadcrumbs={breadcrumbs}
          />
        </RumPageRendered>
      </AppErrorBoundary>
    );
  }

  const tabHref = (target: OutboxTab) => splatHref(tabSegmentFor(canonicalSegment, target));
  const getDeliveryHref = (delivery: Delivery) =>
    splatHref(`${tabSegmentFor(canonicalSegment, resolution.tab)}/${delivery.id}`);

  return (
    <RumPageRendered>
      <OutboxPage tab={resolution.tab} tabHref={tabHref} getDeliveryHref={getDeliveryHref} />
    </RumPageRendered>
  );
}
